import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/auth";
import { formatApiError, useClientAuth } from "@/lib/clientAuth";
import { AuthShell } from "@/pages/portal/ClientLogin";

const inputCls = "w-full rounded-xl border border-gold/20 bg-obsidian-900 px-4 py-3 text-sm text-ivory outline-none transition focus:border-gold/60";
const btnCls = "w-full rounded-full bg-gold px-6 py-3 font-sans text-sm font-semibold uppercase tracking-[0.12em] text-obsidian-900 transition hover:shadow-[0_0_28px_rgba(212,175,55,0.35)] disabled:opacity-50";

export function VerifyEmail() {
  const [params] = useSearchParams();
  const [state, setState] = useState("working");
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get("token");
    if (!token) { setState("error"); return; }
    api.post("/client/auth/verify-email", { token }).then(() => setState("ok")).catch(() => setState("error"));
  }, [params]);
  return (
    <AuthShell kicker="Client Portal" title={state === "ok" ? "Email Verified" : state === "error" ? "Link Invalid" : "Verifying…"} testid="page-verify-email">
      <p className="text-center text-sm text-ivory/70">
        {state === "ok" && "Your email is verified. You can now sign in."}
        {state === "error" && "This verification link is invalid or has expired."}
        {state === "working" && "Please wait…"}
      </p>
      {state !== "working" && <Link to="/login" data-testid="verify-signin" className={`${btnCls} mt-8 block text-center`}>Go to Sign In</Link>}
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/client/auth/forgot-password", { email });
      setSent(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <AuthShell kicker="Client Portal" title="Reset Password" testid="page-forgot">
      {sent ? (
        <p className="text-center text-sm text-ivory/70">If an account exists for <span className="text-gold">{email}</span>, a reset link has been sent.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <input data-testid="forgot-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
          <button data-testid="forgot-submit" className={btnCls}>Send Reset Link</button>
        </form>
      )}
      <p className="mt-6 text-center text-sm text-ivory/60"><Link to="/login" className="text-gold hover:underline">Back to sign in</Link></p>
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const [password, setPassword] = useState("");
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/client/auth/reset-password", { token: params.get("token"), password });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <AuthShell kicker="Client Portal" title="Set New Password" testid="page-reset">
      <form onSubmit={submit} className="space-y-4">
        <input data-testid="reset-password" type="password" required minLength={8} placeholder="New password (min 8)" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
        <button data-testid="reset-submit" className={btnCls}>Update Password</button>
      </form>
    </AuthShell>
  );
}

export function AcceptInvite() {
  const [params] = useSearchParams();
  const { user } = useClientAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState("idle");
  const token = params.get("token");
  const accept = async () => {
    setStatus("working");
    try {
      await api.post("/client/auth/refresh").catch(() => {});
      const { data } = await api.post("/corporate/accept-invite", { token });
      toast.success("Invitation accepted");
      nav("/portal");
      return data;
    } catch (err) {
      setStatus("idle");
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  return (
    <AuthShell kicker="Corporate Portal" title="Accept Invitation" testid="page-accept-invite">
      {user ? (
        <>
          <p className="text-center text-sm text-ivory/70">You're signed in as <span className="text-gold">{user.email}</span>. Accept this invitation to join the company.</p>
          <button data-testid="accept-invite-btn" disabled={status === "working"} onClick={accept} className={`${btnCls} mt-8`}>{status === "working" ? "Joining…" : "Accept Invitation"}</button>
        </>
      ) : (
        <p className="text-center text-sm text-ivory/70">Please <Link to={`/login`} className="text-gold hover:underline">sign in</Link> or <Link to="/register" className="text-gold hover:underline">create an account</Link> with the invited email, then re-open this link.</p>
      )}
    </AuthShell>
  );
}

export function ClientAuthCallback() {
  const { setUser } = useClientAuth();
  const nav = useNavigate();
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) { nav("/login"); return; }
    api.post("/client/auth/google", { session_id: decodeURIComponent(m[1]) })
      .then(({ data }) => { setUser(data); toast.success("Signed in with Google"); nav("/portal"); })
      .catch((err) => { toast.error(formatApiError(err.response?.data?.detail) || "Google sign-in failed"); nav("/login"); });
  }, [nav, setUser]);
  return (
    <main className="flex min-h-screen items-center justify-center bg-obsidian-900">
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Signing you in…</p>
    </main>
  );
}
